import styled from 'styled-components';
import { observer } from 'mobx-react';
import { flatten, get, values } from 'lodash';
import { Trans, useTranslation } from 'react-i18next';
import { GlobalToken, theme } from 'antd';

import ChevronDownIcon from '@/comp/common/icons/ChevronDownIcon';
import EPTierLockCover from '@/comp/common/EPTierLockCover';
import Super from '@/comp/Super';
import { permissions } from '@/constants';
import { ComponentTierName, interestedPackageEnum } from '@/constants/enums';
import { useStores } from '@/hooks/use-stores';
import { useMeasurement } from '@/hooks/useMeasurement';
import { checkTierPermission } from '@/utils/util-functions';
import PowerKpiIcon from '@/comp/common/icons/PowerKpiIcon';
import FlowKpiIcon from '@/comp/common/icons/FlowKpiIcon';
import PressureKpiIcon from '@/comp/common/icons/PressureKpiIcon';

import KPIBoxWithTargetOption from './KPIBoxWithTargetOption';
import { useSpecificPowerKPI } from '../MapKPI/MapKPI.utils';

const { useToken } = theme;

const ControlCenterKPIS = observer(({ isCustomGaugesExpanded, setIsCustomGaugesExpanded }) => {
  const { stationStore, permissionStore } = useStores();
  const shouldRestrictKPIs = checkTierPermission(ComponentTierName.ControlCenterKPIs, stationStore);
  const customMonitors = flatten(values(stationStore?.customMonitoredParameters));
  const { token } = useToken();
  const { t } = useTranslation();
  const { tvalue, tunit } = useMeasurement();
  const { pressure, flow, power } = get(stationStore, 'currentStation.kpis', {});
  const specificPower = useSpecificPowerKPI();

  const hasAccessToCustomGauges = () => {
    return permissionStore.isAuth(permissions.VIEW_CUSTOM_GAUGES);
  };

  const PressureKPI = (
    <KPIBoxWithTargetOption
      key="pressure"
      title={t('PRESSURE')}
      icon={<PressureKpiIcon />}
      value={tvalue(pressure?.currentValue, 'bar')}
      unit={t(tunit('Bar'))}
      targetValue={tvalue(pressure?.targetValue, 'bar')}
    />
  );

  const FlowKPI = (
    <KPIBoxWithTargetOption
      key="flow"
      title={t('FLOW')}
      icon={<FlowKpiIcon />}
      value={tvalue(flow?.currentValue, 'm^3/min')}
      unit={
        <Trans i18nKey={tunit('m3/min')}>
          <Super />
        </Trans>
      }
      tooltip={t(
        flow?.estimatedFlow
          ? 'Compressor kW based calculation'
          : 'Flow based on Flow sensor avg at the end of the station',
      )}
    />
  );

  const PowerKPI = (
    <KPIBoxWithTargetOption
      key="power"
      title={t('POWER')}
      icon={<PowerKpiIcon />}
      value={tvalue(power?.currentValue, 'kw')}
      unit={
        <Trans i18nKey={tunit('kW')}>
          <Super />
        </Trans>
      }
    />
  );

  const SpecificPowerKPI = (
    <KPIBoxWithTargetOption
      key="specificPower"
      title={t('SPECIFIC POWER')}
      value={tvalue(specificPower, 'kW/(m^3/min)')}
      unit={
        <Trans i18nKey={tunit('kW/(m3/min)')}>
          <Super />
        </Trans>
      }
    />
  );

  return (
    <Container>
      <div className="kpis">
        {shouldRestrictKPIs && (
          <EPTierLockCover
            label={t('learnMoreAndEnableKPIs')}
            tooltipTextPart2={t('alwaysPerform')}
            tooltipPlacement="bottom"
            tooltipWidth={300}
            message={t('')}
            emailRequestTypeOfInterestedPackage={interestedPackageEnum.CONTROL_CENTER}
            emailRequestButtonClicked={'Lock Button'}
            emailRequestWhichScreen={'Station KPIs'}
          />
        )}
        {PressureKPI}
        <div className="vertical-separator"></div>
        {FlowKPI}
        <div className="vertical-separator"></div>
        {PowerKPI}
        <div className="vertical-separator"></div>
        {SpecificPowerKPI}
      </div>

      {customMonitors?.length > 0 && hasAccessToCustomGauges() && (
        <ExpandButton
          className={isCustomGaugesExpanded ? 'collapsed' : ''}
          onClick={() => setIsCustomGaugesExpanded(isExpanded => !isExpanded)}
        >
          <ChevronDownIcon color={token.colorPrimary} />
        </ExpandButton>
      )}
    </Container>
  );
});

const Container = styled.div<{ theme: { token: GlobalToken } }>`
  position: relative;
  display: flex;
  flex-direction: column;

  .kpis {
    min-width: 700px;
    display: flex;
    justify-content: space-between;
    border: 1px solid ${({ theme: { token } }) => token.colorBorder};
    border-radius: 2px;
    align-items: center;
    padding: ${({ theme: { token } }) => token.padding}px;
  }

  .kpi-box-with-target-option {
    flex-direction: row;
    margin: 0;

    .title-container {
      display: flex;
      align-items: center;
      justify-content: left;
      margin: ${({ theme: { token } }) => token.marginXXS}px ${({ theme: { token } }) => token.marginSM}px 0 0;
      padding-top: ${({ theme: { token } }) => token.paddingXXS}px;

      .title {
        margin-left: ${({ theme: { token } }) => token.marginSM}px;
        padding: 0;
        text-transform: uppercase;
      }
    }

    .target-container {
      margin: 0 0 0 ${({ theme: { token } }) => token.margin}px;
      display: inline-flex;
      padding-bottom: ${({ theme: { token } }) => token.paddingXXS}px;
      height: 100%;
      justify-content: space-evenly;

      .target-title {
        color: ${({ theme: { token } }) => token.colorBorder};
        font-weight: ${({ theme: { token } }) => token.fontWeightStrong};
      }

      .target-value-container {
        display: flex;
        align-items: flex-end;
        margin-bottom: ${({ theme: { token } }) => token.marginXXS}px;
      }

      .target-value {
        font-size: ${({ theme: { token } }) => token.fontSize}px;
        color: ${({ theme: { token } }) => token.colorTextSecondary};
      }

      .unit {
        font-size: ${({ theme: { token } }) => token.fontSizeSM}px;
        color: ${({ theme: { token } }) => token.colorTextSecondary};
        margin: 0 0 ${({ theme: { token } }) => token.marginXXS}px ${({ theme: { token } }) => token.marginXXS}px;
      }
    }

    .value-container {
      display: flex;
      align-items: stretch;
      margin: 0;
      height: 100%;

      .value {
        font-size: ${({ theme: { token } }) => token.fontSizeHeading2}px;
        font-weight: ${({ theme: { token } }) => token.fontWeightStrong};
        color: ${({ theme: { token } }) => token.colorText};
      }

      .unit {
        font-size: ${({ theme: { token } }) => token.fontSize}px;
        color: ${({ theme: { token } }) => token.colorTextSecondary};
        margin-left: ${({ theme: { token } }) => token.marginXXS}px;
        height: 100%;
        padding-top: ${({ theme: { token } }) => token.paddingLG}px;
      }
    }
  }

  .vertical-separator {
    background-color: ${({ theme: { token } }) => token.colorBorder};
    width: 1px;
    height: 100%;
    margin: 0 ${({ theme: { token } }) => token.marginLG}px;
  }
`;

const ExpandButton = styled.button<{ theme: { token: GlobalToken } }>`
  border: none;
  cursor: pointer;
  background-color: ${({ theme: { token } }) => token.colorBgElevated};
  border-radius: 50%;
  position: absolute;
  top: 100px;
  left: 0;
  right: 0;
  margin: auto;
  width: 30px;
  height: 30px;
  padding: 0;
  box-shadow: ${({ theme: { token } }) => token.boxShadowTertiary};

  svg {
    transition: transform 0.15s ease-out;
    width: 30px;
    height: 30px;
  }

  &.collapsed {
    transform: rotate(180deg);
  }
`;

export default ControlCenterKPIS;
